import { Button, ComboField, Input } from "@legal-500/legal500-design-system";
import type { AiListedModel, AiProviderModels } from "@/api/aiParser.service";
import styles from "./AiSchemas.module.css";

type Props = {
  value: string;
  onChange: (next: string) => void;
  error?: string;
  /** The listing for the provider currently selected on the form, or null if it answered nothing. */
  providerModels: AiProviderModels | null;
  loading: boolean;
  /** A failure of the listing *request*, as opposed to one provider answering `available: false`. */
  loadError: string | null;
  /** True when the user has asked to type a name rather than pick one. */
  manual: boolean;
  onManualChange: (next: boolean) => void;
  onRefresh: () => void;
};

const DEPLOYMENT_HINT = "The Azure deployment name, exactly as it was created.";

/** An em dash rather than brackets: the two names are of equal standing, one is just the value. */
function optionLabel(model: AiListedModel): string {
  return model.baseModel ? `${model.id} — ${model.baseModel}` : model.id;
}

// One dialog, one Model field — a fixed id is safe and keeps the label bound across the swap
// between the dropdown and the free-text input.
const FIELD_ID = "ai-schema-model";

/**
 * Model is the one field on this form whose valid values live outside the database.
 *
 * Azure deployment names are chosen by whoever created the deployment, and the listing endpoint is
 * a convenience over whatever that Azure resource happens to expose — it can be unavailable, stale,
 * or simply not know about a deployment made five minutes ago. So the listing is never allowed to
 * become a gate: a picked name and a typed name are equally valid, and every path here ends in a
 * control the user can put an arbitrary string into.
 */
export function ModelField({
  value,
  onChange,
  error,
  providerModels,
  loading,
  loadError,
  manual,
  onManualChange,
  onRefresh,
}: Props) {
  const listed = providerModels?.available === true ? providerModels.models : [];
  const canPick = listed.length > 0;
  const showDropdown = canPick && !manual;

  function freeTextHelper(): string {
    if (loading) return "Loading deployments…";
    if (loadError) return `${DEPLOYMENT_HINT} The model list could not be loaded: ${loadError}`;
    if (providerModels && !providerModels.available) {
      return providerModels.error
        ? `${DEPLOYMENT_HINT} The model list is unavailable: ${providerModels.error}`
        : `${DEPLOYMENT_HINT} The model list is unavailable for this provider.`;
    }
    if (providerModels && listed.length === 0) {
      return `${DEPLOYMENT_HINT} No deployments were listed for this provider.`;
    }
    return DEPLOYMENT_HINT;
  }

  function status(): string {
    if (loading) return "Loading deployments…";
    if (loadError || providerModels?.available === false) return "Listing unavailable";
    if (!canPick) return "No deployments listed";
    return `${listed.length} deployment${listed.length === 1 ? "" : "s"} listed`;
  }

  return (
    <div className={styles.modelField}>
      {/*
       * The label is rendered here rather than by the control, because the control swaps between a
       * combobox and a plain input and the label must not swap with it. ComboField also puts its
       * `label` on the listbox as an aria-label, which would give the dialog two elements answering
       * to "Model"; naming the input once, by id, keeps exactly one.
       */}
      <label className={styles.fieldLabel} htmlFor={FIELD_ID}>
        Model
      </label>
      {showDropdown ? (
        <ComboField
          id={FIELD_ID}
          value={value}
          onChange={onChange}
          /*
           * The value is the deployment name and nothing else — that is what gets saved and sent.
           * The base model rides along in the label because a deployment name alone
           * ("l500-dev-platform-core-openai-gpt-4o-mini") says nothing about what it runs, and the
           * label is also what the combobox filters on, so typing "4o-mini" finds it.
           */
          options={listed.map(model => ({ value: model.id, label: optionLabel(model) }))}
          error={error}
          // A deployment the listing does not know about is a legitimate answer, not a mistake.
          warnOnUnknownValue={false}
          helperText={DEPLOYMENT_HINT}
          fullWidth
        />
      ) : (
        <Input
          id={FIELD_ID}
          value={value}
          onChange={event => onChange(event.target.value)}
          error={error}
          helperText={freeTextHelper()}
          fullWidth
        />
      )}

      <div className={styles.fieldAside}>
        <span className={styles.fieldAsideStatus}>{status()}</span>
        <span className={styles.fieldAsideActions}>
          {canPick &&
            (manual ? (
              <Button
                type="button"
                size="small"
                variant="text"
                onClick={() => onManualChange(false)}
              >
                Choose from the list
              </Button>
            ) : (
              <Button
                type="button"
                size="small"
                variant="text"
                onClick={() => onManualChange(true)}
              >
                Enter a name manually
              </Button>
            ))}
          <Button
            type="button"
            size="small"
            variant="text"
            onClick={onRefresh}
            disabled={loading}
            aria-label="Refresh model list"
          >
            Refresh
          </Button>
        </span>
      </div>
    </div>
  );
}
